import type { ChatMessageToastData } from '@/modules/chat-matrix-fast/composables/useChatMessageToast'
import { soundService } from '@/utils/soundService'

const NOTIFICATION_TAG_PREFIX = 'chat-message-'
const SW_READY_TIMEOUT_MS = 400
export const CHAT_NOTIFICATION_CLICK_TYPE = 'CHAT_NOTIFICATION_CLICK'

let permissionGestureBound = false
let webPushEnsureInFlight: Promise<void> | null = null

function isNotificationApiAvailable(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
}

function isTabHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState !== 'visible'
}

/** OS-баннер только при скрытой вкладке; на активной — in-app toast. */
export function shouldUseChatSystemNotification(): boolean {
  return isTabHidden()
}

function buildMessengerUrl(chatId?: number | string | null, roomId?: string): string {
  const params = new URLSearchParams()
  params.set('notifyOpen', '1')
  if (chatId != null && chatId !== '') {
    params.set('chatId', String(chatId))
  }
  if (roomId) {
    params.set('roomId', roomId)
  }
  return `/messenger?${params.toString()}`
}

export function getChatNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isNotificationApiAvailable()) {
    return 'unsupported'
  }

  return Notification.permission
}

/** Web Push + Matrix Sygnal pusher — канал при полностью закрытом браузере. */
async function ensureChatWebPushSubscription(): Promise<void> {
  if (webPushEnsureInFlight) {
    return webPushEnsureInFlight
  }

  webPushEnsureInFlight = (async () => {
    try {
      const { logtoClient } = await import('@/plugins/logto')
      const isAuth = logtoClient ? await logtoClient.isAuthenticated() : false
      if (!isAuth) {
        return
      }

      const { usePushSubscription } = await import('@/composables/usePushSubscription')
      await usePushSubscription().ensureSubscription()
      const { ensureMatrixSygnalPusher } = await import(
        '@/modules/chat-matrix-fast/services/matrixSygnalPusher'
      )
      await ensureMatrixSygnalPusher()
    } catch {
      // VAPID/SW/Sygnal могут быть не настроены — локальные Notification всё равно работают
    } finally {
      webPushEnsureInFlight = null
    }
  })()

  return webPushEnsureInFlight
}

/** Запрос permission только из user gesture. */
export async function requestChatNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!isNotificationApiAvailable()) {
    return 'unsupported'
  }

  if (Notification.permission === 'granted') {
    void soundService.ensureNotifications()
    void ensureChatWebPushSubscription()
    return 'granted'
  }

  if (Notification.permission === 'denied') {
    return 'denied'
  }

  const permission = await Notification.requestPermission()
  if (permission === 'granted') {
    void soundService.ensureNotifications()
    void ensureChatWebPushSubscription()
  }

  return permission
}

/** После жеста на странице запрашивает OS-permission, пока статус `default`. */
export function bindChatSystemNotificationPermissionGesture(): void {
  if (permissionGestureBound || typeof window === 'undefined' || !isNotificationApiAvailable()) {
    return
  }

  permissionGestureBound = true

  if (Notification.permission === 'granted') {
    void ensureChatWebPushSubscription()
    return
  }

  if (Notification.permission !== 'default') {
    return
  }

  const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown']
  const onGesture = () => {
    if (Notification.permission !== 'default') {
      events.forEach(event => window.removeEventListener(event, onGesture))
      if (Notification.permission === 'granted') {
        void ensureChatWebPushSubscription()
      }
      return
    }

    // В том же тике жеста — иначе Chrome/Safari глушат диалог.
    void Notification.requestPermission().then(permission => {
      if (permission !== 'default') {
        events.forEach(event => window.removeEventListener(event, onGesture))
      }
      if (permission === 'granted') {
        void soundService.ensureNotifications()
        void ensureChatWebPushSubscription()
      }
    })
  }

  events.forEach(event => window.addEventListener(event, onGesture, { passive: true }))
}

/** SW ready без таймаута может висеть вечно, если sw не зарегистрировался. */
async function resolveRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null
  }

  try {
    const existing = await navigator.serviceWorker.getRegistration()
    if (existing?.active || existing?.waiting || existing?.installing) {
      return existing
    }
  } catch {
    // ignore
  }

  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>(resolve => {
        window.setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS)
      }),
    ])
  } catch {
    return null
  }
}

function attachNotificationClick(
  notification: Notification,
  data: ChatMessageToastData,
): void {
  notification.onclick = () => {
    window.focus()
    notification.close()
    window.dispatchEvent(new CustomEvent(CHAT_NOTIFICATION_CLICK_TYPE, {
      detail: {
        chatId: data.chatId,
        roomId: data.roomId,
      },
    }))
  }
}

/** OS-уведомление при скрытой вкладке; без granted — тихо выходим. */
export async function showChatSystemNotification(data: ChatMessageToastData): Promise<void> {
  if (!isNotificationApiAvailable() || !shouldUseChatSystemNotification()) {
    return
  }

  if (Notification.permission !== 'granted') {
    return
  }

  const title = data.senderName || 'Новое сообщение'
  const body = data.messagePreview || 'Новое сообщение'
  const tag = `${NOTIFICATION_TAG_PREFIX}${data.chatId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
  const url = buildMessengerUrl(data.chatId, data.roomId)
  const icon = data.avatar && /^https?:\/\//i.test(data.avatar)
    ? data.avatar
    : '/favicon.ico'

  const options: NotificationOptions & { renotify?: boolean } = {
    body,
    icon,
    badge: '/favicon.ico',
    tag,
    renotify: true,
    silent: false,
    data: {
      url,
      chatId: data.chatId,
      roomId: data.roomId || null,
      type: CHAT_NOTIFICATION_CLICK_TYPE,
    },
  }

  try {
    const notification = new Notification(title, options)
    attachNotificationClick(notification, data)
    return
  } catch {
    // fallback на SW
  }

  try {
    const registration = await resolveRegistration()
    if (registration?.showNotification) {
      await registration.showNotification(title, options)
    }
  } catch {
    // Браузер мог запретить показ
  }
}
